import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronRight } from "lucide-react";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { IMAGES } from "@/data/images";
import { DownloadButton } from "@/components/shared/DownloadButton";

export const Header = () => {
  const { t } = useTranslation();
  const { lang = "fr" } = useParams();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: `/${lang}/features`, label: t("nav.features") },
    { to: `/${lang}/about`, label: t("nav.about") },
    { to: `/${lang}/pricing`, label: t("nav.pricing") },
    { to: `/${lang}/pro`, label: t("nav.pro") },
    { to: `/${lang}/tutorials`, label: t("nav.tutorials") },
    { to: `/${lang}/blog`, label: t("nav.blog") },
    { to: `/${lang}/faq`, label: t("nav.faq") },
    { to: `/${lang}/press`, label: t("nav.press") },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/60 shadow-sm" : "bg-white/60 backdrop-blur-lg"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 h-[76px]">
        <Link to={`/${lang}`} data-testid="header-logo-link" className="flex items-center gap-2.5 shrink-0">
          <img src={IMAGES.logo} alt="Fastority" className="h-8 w-8 object-contain" />
          <span className="font-heading font-extrabold text-lg tracking-tight text-slate-900">Fastority</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label}`}
              className="text-sm font-medium text-slate-600 hover:text-deepblue transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 w-0 h-[2px] bg-coral transition-all duration-300 group-hover:w-full" />
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          <DownloadButton
            testId="header-download-cta"
            align="end"
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-coral transition-colors duration-300"
          >
            {t("nav.download")}
            <ChevronRight size={15} />
          </DownloadButton>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-slate-800"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-t border-slate-200 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-5 py-4 gap-1">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label}`}
                  className="py-3 text-base font-medium text-slate-700 border-b border-slate-100"
                >
                  {l.label}
                </Link>
              ))}
              <div className="flex items-center justify-between pt-4">
                <LanguageSwitcher />
                <DownloadButton
                  testId="mobile-menu-download-cta"
                  className="bg-coral text-white text-sm font-semibold px-5 py-2.5 rounded-full"
                >
                  {t("nav.download")}
                </DownloadButton>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
